import { z } from 'zod';

/**
 * Columnas comunes de toda entidad sincronizable.
 * Ver docs/DATA_MODEL.md §1.1 y docs/SYNC_ENGINE.md §2.
 */
export const syncColumnsSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  createdAt: z.iso.datetime({ offset: true }),
  updatedAt: z.iso.datetime({ offset: true }),
  deletedAt: z.iso.datetime({ offset: true }).nullable(),
  rev: z.number().int().positive(),
  originDeviceId: z.string().min(1).max(64),
  /** Lo asigna el servidor. Null mientras la fila solo existe en local. */
  syncSeq: z.number().int().nonnegative().nullable(),
});

export type SyncColumns = z.infer<typeof syncColumnsSchema>;

/** Fecha del calendario local del usuario (no UTC): YYYY-MM-DD. */
export const localDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'formato esperado YYYY-MM-DD');

export type LocalDate = z.infer<typeof localDateSchema>;

/**
 * Entidades sincronizables. El servidor mantiene la misma lista en
 * `public.synced_entities`; ampliar una sin la otra rompe la sincronizacion.
 */
export const entityNameSchema = z.enum([
  // Fase 1
  'profiles',
  'user_preferences',
  'gyms',
  'equipment',
  'available_loads',
  // Fase 2 — programacion
  'training_blocks',
  'training_weeks',
  'training_sessions',
  'session_exercises',
  'set_prescriptions',
  'warmup_prescriptions',
  // Fase 2 — recuperacion y ejecucion
  'readiness_checkins',
  'workout_logs',
  'performed_sets',
  'rest_events',
  'load_recommendations',
  'progression_state',
  // Fase 3 — motor de programacion
  'training_decisions',
  'cardio_prescriptions',
  'cardio_logs',
  // Fase 4 — nutricion
  'nutrition_targets',
  'meal_plans',
  'planned_meals',
  'meal_logs',
  'daily_weights',
  'user_recipe_settings',
  // Fase 5 — compra y despensa
  'pantry_items',
  'shopping_lists',
  'shopping_list_items',
  // Fase 6 — progreso
  'body_measurements',
  'weekly_checkins',
  // Pasos diarios (§17). Llego despues de la Fase 8, pero es progreso.
  'daily_activity',
  // Fase 7 — IA. Las tres se sincronizan, pero solo `ai_decisions` la escribe
  // el cliente: las otras dos las escribe la Edge Function y aqui solo BAJAN
  // (migracion 0020). Registrarlas es lo que permite ver el consumo sin
  // conexion; el privilegio es lo que impide subirlas.
  // Evidencia cientifica (§8). `evidence_sources.search_id` apunta a
  // `evidence_searches`, asi que la busqueda viaja antes que sus fichas.
  'evidence_searches',
  'evidence_sources',
  'ai_requests',
  'ai_decisions',
  'ai_usage_budget',
]);

export type EntityName = z.infer<typeof entityNameSchema>;

/**
 * Entidades de solo insercion: su historial no se reescribe
 * (docs/SYNC_ENGINE.md §5.1). Se declara aqui para que cliente y servidor
 * compartan la misma nocion.
 */
export const APPEND_ONLY_ENTITIES: readonly EntityName[] = [
  'performed_sets',
  'rest_events',
  'load_recommendations',
  'readiness_checkins',
  // Una llamada a la IA ocurrio y costo lo que costo. Reescribirla seria
  // reescribir la factura.
  // Evidencia cientifica (§8). `evidence_sources.search_id` apunta a
  // `evidence_searches`, asi que la busqueda viaja antes que sus fichas.
  'evidence_searches',
  'evidence_sources',
  'ai_requests',
];
